import React, { useState } from 'react';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import openai from '../../../services/openaiClient';

const AllAlertsActionsModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [aiQuery, setAiQuery] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [bulkAction, setBulkAction] = useState('');

  const handleAIAnalysis = async () => {
    if (!aiQuery?.trim()) return;
    
    setIsLoadingAI(true);
    try {
      const completion = await openai?.chat?.completions?.create({
        model: 'gpt-3.5-turbo',
        messages: [
          {
            role: 'system',
            content: 'أنت مساعد ذكي لإدارة التنبيهات والإشعارات في نظام إدارة عقارات. ساعد في تحليل التنبيهات وتقديم توصيات.'
          },
          {
            role: 'user',
            content: aiQuery
          }
        ],
        temperature: 0.7,
        max_tokens: 500
      });
      
      setAiResponse(completion?.choices?.[0]?.message?.content || 'لم يتم الحصول على رد');
    } catch (error) {
      console.error('AI Analysis Error:', error);
      setAiResponse('حدث خطأ في تحليل الذكاء الاصطناعي. يرجى المحاولة مرة أخرى.');
    } finally {
      setIsLoadingAI(false);
    }
  };

  const handleBulkAction = () => {
    if (!bulkAction) return;
    console.log('Executing bulk action:', bulkAction);
    alert(`تم تنفيذ الإجراء: ${bulkAction}`);
  };

  const tabs = [
    { id: 'overview', label: 'نظرة عامة', icon: 'LayoutDashboard' },
    { id: 'ai-analysis', label: 'تحليل ذكي', icon: 'Brain' },
    { id: 'bulk-actions', label: 'إجراءات جماعية', icon: 'CheckSquare' }
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="جميع التنبيهات"
      icon="Bell"
      iconColor="#3B82F6"
      size="xl"
      footer={null}
    >
      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-border">
        {tabs?.map(tab => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
              activeTab === tab?.id
                ? 'border-primary text-primary font-semibold' :'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={tab?.icon} size={18} />
            <span>{tab?.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === 'overview' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground mb-4">إحصائيات التنبيهات</h3>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="Bell" size={20} className="text-blue-600" />
                  <span className="text-sm text-muted-foreground">الإجمالي</span>
                </div>
                <p className="text-2xl font-bold text-blue-600">156</p>
              </div>
              
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="AlertCircle" size={20} className="text-red-600" />
                  <span className="text-sm text-muted-foreground">عاجل</span>
                </div>
                <p className="text-2xl font-bold text-red-600">23</p>
              </div>
              
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="Clock" size={20} className="text-yellow-600" />
                  <span className="text-sm text-muted-foreground">قيد الانتظار</span>
                </div>
                <p className="text-2xl font-bold text-yellow-600">45</p>
              </div>
              
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="CheckCircle" size={20} className="text-green-600" />
                  <span className="text-sm text-muted-foreground">تم الحل</span>
                </div>
                <p className="text-2xl font-bold text-green-600">88</p>
              </div>
            </div>

            <div className="mt-6">
              <h4 className="text-md font-semibold mb-3">التصفية السريعة</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">الأولوية</label>
                  <select
                    value={selectedPriority}
                    onChange={(e) => setSelectedPriority(e?.target?.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background"
                  >
                    <option value="all">الكل</option>
                    <option value="urgent">عاجل</option>
                    <option value="high">عالي</option>
                    <option value="medium">متوسط</option>
                    <option value="low">منخفض</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">الحالة</label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e?.target?.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background"
                  >
                    <option value="all">الكل</option>
                    <option value="unread">غير مقروء</option>
                    <option value="acknowledged">تم الإقرار</option>
                    <option value="in_progress">قيد التنفيذ</option>
                    <option value="resolved">تم الحل</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'ai-analysis' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground mb-4">تحليل ذكي للتنبيهات</h3>
            
            <div className="space-y-3">
              <label className="block text-sm font-medium">اسأل الذكاء الاصطناعي</label>
              <Input
                value={aiQuery}
                onChange={(e) => setAiQuery(e?.target?.value)}
                placeholder="مثال: ما هي التنبيهات الأكثر أهمية التي يجب معالجتها اليوم؟"
                className="w-full"
              />
              <Button
                onClick={handleAIAnalysis}
                disabled={isLoadingAI || !aiQuery?.trim()}
                className="w-full"
              >
                {isLoadingAI ? (
                  <span className="flex items-center gap-2">
                    <Icon name="Loader" size={18} className="animate-spin" />
                    جاري التحليل...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Icon name="Sparkles" size={18} />
                    تحليل بالذكاء الاصطناعي
                  </span>
                )}
              </Button>
            </div>

            {aiResponse && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-2 mb-2">
                  <Icon name="Brain" size={20} className="text-blue-600 mt-1" />
                  <h4 className="font-semibold text-blue-900">نتيجة التحليل</h4>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{aiResponse}</p>
              </div>
            )}

            <div className="mt-6 p-4 bg-muted/30 rounded-lg">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Icon name="Lightbulb" size={18} className="text-yellow-600" />
                أمثلة على الأسئلة
              </h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• ما هي التنبيهات العاجلة التي تحتاج إلى متابعة فورية؟</li>
                <li>• كيف يمكنني تحسين إدارة التنبيهات؟</li>
                <li>• ما هي الأنماط الشائعة في التنبيهات الأخيرة؟</li>
                <li>• اقترح خطة عمل لحل التنبيهات المتراكمة</li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'bulk-actions' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground mb-4">إجراءات جماعية</h3>
            
            <div className="space-y-3">
              <label className="block text-sm font-medium">اختر الإجراء</label>
              <select
                value={bulkAction}
                onChange={(e) => setBulkAction(e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background"
              >
                <option value="">-- اختر إجراء --</option>
                <option value="mark-read">تعيين كمقروء</option>
                <option value="mark-acknowledged">تعيين كمُقر به</option>
                <option value="assign-staff">توكيل لموظف</option>
                <option value="change-priority">تغيير الأولوية</option>
                <option value="export">تصدير المحدد</option>
                <option value="archive">أرشفة</option>
              </select>
              
              <Button
                onClick={handleBulkAction}
                disabled={!bulkAction}
                className="w-full"
                variant="primary"
              >
                <span className="flex items-center gap-2">
                  <Icon name="Play" size={18} />
                  تنفيذ الإجراء
                </span>
              </Button>
            </div>

            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start gap-2">
                <Icon name="AlertTriangle" size={20} className="text-yellow-600 mt-1" />
                <div>
                  <h4 className="font-semibold text-yellow-900 mb-1">تنبيه</h4>
                  <p className="text-sm text-yellow-800">
                    سيتم تطبيق الإجراء على جميع التنبيهات المحددة. تأكد من اختيارك قبل التنفيذ.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-4">
              <h4 className="font-semibold mb-3">الإجراءات المتاحة</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 border border-border rounded-lg hover:bg-muted/30 cursor-pointer transition-colors">
                  <Icon name="Check" size={18} className="text-green-600 mb-1" />
                  <p className="text-sm font-medium">تعيين كمقروء</p>
                </div>
                <div className="p-3 border border-border rounded-lg hover:bg-muted/30 cursor-pointer transition-colors">
                  <Icon name="UserPlus" size={18} className="text-blue-600 mb-1" />
                  <p className="text-sm font-medium">توكيل لموظف</p>
                </div>
                <div className="p-3 border border-border rounded-lg hover:bg-muted/30 cursor-pointer transition-colors">
                  <Icon name="Flag" size={18} className="text-orange-600 mb-1" />
                  <p className="text-sm font-medium">تغيير الأولوية</p>
                </div>
                <div className="p-3 border border-border rounded-lg hover:bg-muted/30 cursor-pointer transition-colors">
                  <Icon name="Archive" size={18} className="text-gray-600 mb-1" />
                  <p className="text-sm font-medium">أرشفة</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AllAlertsActionsModal;